import { useTranslation } from "react-i18next";

/**
 * Error page shown when a Drive share-link can't be resolved — the link UID is
 * invalid or the link has expired/been deleted (the resolve endpoint answers
 * 404). Mirrors the Adobe XD design: an explanatory text block beside a large
 * neutral circle holding the puzzle illustration. Rendered inside
 * {@link TransferPageSkeleton} so it carries the same top bar and footer as the
 * share-link page.
 *
 * Responsive: desktop is two columns (text left, circle right); on narrow
 * (mobile) widths text and circle overlap (grid-stacked, both centered) so the
 * text reads on top of the puzzle circle.
 */
export default function ShareLinkErrorPage() {
  const { t } = useTranslation();

  return (
    <div className="grid w-full place-items-center px-6 py-10 font-ibm-plex-sans text-[#1A1A1A] lg:flex lg:flex-row lg:items-center lg:justify-between lg:px-[110px] lg:py-0">
      {/* Text — on mobile it shares the single grid cell with the circle (and
          sits on top via z-10); on desktop it's the left column. */}
      <div className="z-10 col-start-1 row-start-1 w-full max-w-[520px] text-center lg:flex-1 lg:text-left">
        <h1 className="text-[26px] font-medium leading-[34px]">
          {t("This link cannot be opened")}
        </h1>
        <p className="mt-7 text-[20px] font-normal leading-[28px]">
          {t("Please check the URL and open the link again.")}
        </p>
        <p className="mt-9 text-[20px] font-medium leading-[28px]">
          {t("Still not working?")}
        </p>
        <p className="mt-7 text-[20px] font-normal leading-[28px]">
          {t("The link may have been deleted or may already have expired.")}
        </p>
        <p className="mt-7 text-[20px] font-normal leading-[28px]">
          {t("Please contact the sender to receive a new link.")}
        </p>
      </div>

      {/* Puzzle illustration in a neutral circle — behind the text on mobile
          (same grid cell), the right column on desktop. */}
      <div className="col-start-1 row-start-1 flex aspect-square w-[300px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F4] sm:w-[380px] lg:w-[560px]">
        <PuzzleIcon className="h-auto w-[150px] sm:w-[200px] lg:w-[260px]" />
      </div>
    </div>
  );
}

/** The two-piece puzzle illustration (one white piece, one primary-tint piece). */
function PuzzleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 258.315 134.715"
      className={className}
      role="img"
      aria-hidden
    >
      <g transform="translate(-602 -293.685)">
        <path
          d="M48.118,26.265a7.408,7.408,0,0,0-3.308-5.714A10.761,10.761,0,0,1,40.1,12.03C40.1,5.388,47.291,0,56.138,0S72.177,5.388,72.177,12.03a10.761,10.761,0,0,1-4.712,8.521,7.408,7.408,0,0,0-3.308,5.714,5.818,5.818,0,0,0,5.814,5.814H84.207a12.033,12.033,0,0,1,12.03,12.03V58.343a5.818,5.818,0,0,0,5.814,5.814,7.408,7.408,0,0,0,5.714-3.308,10.761,10.761,0,0,1,8.521-4.712c6.641,0,12.03,7.193,12.03,16.039s-5.388,16.039-12.03,16.039a10.761,10.761,0,0,1-8.521-4.712,7.408,7.408,0,0,0-5.714-3.308,5.818,5.818,0,0,0-5.814,5.814v30.274a12.033,12.033,0,0,1-12.03,12.03H69.972a5.818,5.818,0,0,1-5.814-5.814,7.408,7.408,0,0,1,3.308-5.714,10.761,10.761,0,0,0,4.712-8.521c0-6.641-7.193-12.03-16.039-12.03S40.1,101.625,40.1,108.266a10.761,10.761,0,0,0,4.712,8.521,7.408,7.408,0,0,1,3.308,5.714,5.818,5.818,0,0,1-5.814,5.814H12.03A12.033,12.033,0,0,1,0,116.286V86.011A5.818,5.818,0,0,1,5.814,80.2a7.408,7.408,0,0,1,5.714,3.308,10.761,10.761,0,0,0,8.521,4.712c6.641,0,12.03-7.193,12.03-16.039s-5.388-16.039-12.03-16.039a10.761,10.761,0,0,0-8.521,4.712,7.408,7.408,0,0,1-5.714,3.308A5.818,5.818,0,0,1,0,58.343V44.108a12.033,12.033,0,0,1,12.03-12.03H42.3A5.818,5.818,0,0,0,48.118,26.265Z"
          transform="translate(732 293.685)"
          fill="#fff"
        />
        <path
          d="M61.235,64A5.369,5.369,0,0,1,66.6,69.365a6.836,6.836,0,0,1-3.053,5.272A9.929,9.929,0,0,0,59.2,82.5c0,6.128,6.637,11.1,14.8,11.1s14.8-4.972,14.8-11.1a9.929,9.929,0,0,0-4.347-7.862A6.836,6.836,0,0,1,81.4,69.365,5.369,5.369,0,0,1,86.765,64H107.3a11.1,11.1,0,0,1,11.1,11.1V95.635A5.369,5.369,0,0,0,123.765,101a6.836,6.836,0,0,0,5.272-3.053A9.929,9.929,0,0,1,136.9,93.6c6.128,0,11.1,6.637,11.1,14.8s-4.972,14.8-11.1,14.8a9.929,9.929,0,0,1-7.863-4.348,6.836,6.836,0,0,0-5.272-3.053,5.369,5.369,0,0,0-5.365,5.365V141.7a11.1,11.1,0,0,1-11.1,11.1H86.765a5.369,5.369,0,0,1-5.365-5.365,6.836,6.836,0,0,1,3.053-5.272A9.929,9.929,0,0,0,88.8,134.3c0-6.128-6.637-11.1-14.8-11.1s-14.8,4.972-14.8,11.1a9.929,9.929,0,0,0,4.347,7.863,6.836,6.836,0,0,1,3.053,5.272,5.369,5.369,0,0,1-5.365,5.365H40.7a11.1,11.1,0,0,1-11.1-11.1V121.165a5.369,5.369,0,0,0-5.365-5.365,6.836,6.836,0,0,0-5.273,3.053A9.929,9.929,0,0,1,11.1,123.2C4.972,123.2,0,116.563,0,108.4S4.972,93.6,11.1,93.6a9.929,9.929,0,0,1,7.863,4.348A6.836,6.836,0,0,0,24.235,101,5.369,5.369,0,0,0,29.6,95.635V75.1A11.1,11.1,0,0,1,40.7,64Z"
          transform="translate(602 275.6)"
          fill="#ccf0ef"
        />
      </g>
    </svg>
  );
}
